/**
 * API utenti per il frontend (Agenzia / Operatore: lista utenti sotto di sé, crea utente).
 * Base URL: stesso del login. Endpoint: /admin/users, che filtra già sul
 * sottoalbero di chi chiede.
 * 
 * Le operazioni finanziarie (giroconto, fido, bonus) usano credit-service Java (8091)
 * via API Gateway per performance in produzione.
 */
import axios from 'axios';
import creditApi from './creditApi';
import { getCurrentUserId } from '../utils/currentUser';
import i18next from 'i18next';
import {nellaLingua} from './erroriConto';

function getBaseUrl() {
  const env = typeof window !== 'undefined' && window.env ? window.env : {};
  return env.REACT_APP_BACKOFFICE_API_URL || env.REACT_APP_API_URL || '';
}

function getApiGatewayUrl() {
  const env = typeof window !== 'undefined' && window.env ? window.env : {};
  return env.REACT_APP_API_URL || 'http://localhost:9001';
}

function getAuthHeaders() {
  const token = typeof window !== 'undefined' ? window.localStorage.getItem('access_token') : null;
  const userId = typeof window !== 'undefined' ? window.localStorage.getItem('user_id') : null;
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (userId) headers['X-User-Id'] = userId;
  const tenant = typeof window !== 'undefined' && window.env && window.env.REACT_APP_API_TENANT;
  if (tenant) headers.TENANT_ID = tenant;
  headers['X-Client-Surface'] = 'FRONTEND';
  return headers;
}

// Flag per usare API Java (true) o Node.js legacy (false)
const USE_JAVA_CREDIT_API = true;

/**
 * Lista utenti (con filtri opzionali).
 * @param {{ page?: number, limit?: number, search?: string, name?: string, username?: string, parentUserId?: string }} params
 * @returns {Promise<{ users: Array, total: number, page, limit, totalPages }>}
 */
export async function getUsers(params = {}) {
  const base = getBaseUrl();
  if (!base) throw new Error(i18next.t('API URL non configurata'));
  const url = `${base}/admin/users`;
  const q = { ...params };
  if (q.name || q.username) q.search = q.search || q.username || q.name;
  const res = await axios.get(url, { params: q, headers: getAuthHeaders() });
  if (Array.isArray(res.data)) {
    return { users: res.data, total: res.data.length, page: params.page || 1, limit: params.limit || 20, totalPages: 1 };
  }
  return res.data;
}

/**
 * Crea utente (es. Agenzia crea Player). parentUserId = current user id per gerarchia.
 * @param {{ username: string, email: string, password: string, role?: string, parentUserId?: string }} data
 * @returns {Promise<Object>}
 */
export async function createUser(data) {
  const base = getBaseUrl();
  if (!base) throw new Error(i18next.t('API URL non configurata'));

  // Creare una posizione sono due cose: una persona che può entrare, e un posto
  // per lei nella rete. Ogni servizio risponde della propria — user-management
  // delle credenziali, jurisdiction della gerarchia.
  const registrazione = await axios.post(
    `${base}/users-management-service/api/users/register`,
    {
      username: data.username,
      email: data.email,
      password: data.password,
      confirmPassword: data.confirmPassword || data.password,
      acceptTerms: true,
    },
    { headers: getAuthHeaders() }
  );

  const nuovoUserId = registrazione.data?.user?.userId;
  if (!nuovoUserId) {
    throw new Error(nellaLingua(registrazione.data?.message) || i18next.t('Registrazione non riuscita'));
  }

  // Da qui in poi la persona ESISTE GIA'. Se il secondo passaggio fallisce
  // non si puo' tornare indietro - user-management non offre nessun modo di
  // disfare una registrazione - quindi l'errore deve dire esattamente cosa e'
  // rimasto a meta', con lo username dentro: un "creazione non riuscita"
  // generico farebbe riprovare con lo stesso nome, che a quel punto risulta
  // gia' preso, e nessuno capirebbe piu' niente.
  let nodo;
  try {
    nodo = await axios.post(
      `${base}/private/jurisdiction`,
      {
        userId: nuovoUserId,
        // Il nodo porta lo USERNAME. Un campo "nome" a parte c'era, ed e'
        // stato tolto perche' ci si scriveva il livello - "master", "agente" -
        // che finiva nelle causali dei movimenti al posto del nome vero.
        name: data.username,
        type: data.type,
        parentId: data.parentUserId || currentUserId(),
      },
      { headers: getAuthHeaders() }
    );
  } catch (e) {
    const dettaglio = (e && e.response && e.response.data && e.response.data.message) || e.message;
    const guaio = new Error(
        i18next.t('L\'utente "{{username}}" è stato creato, ma NON è stato collegato ', {username: data.username})
        + i18next.t('alla rete: {{dettaglio}}. Va sistemato dal backoffice — non riprovare ', {dettaglio})
        + i18next.t('con lo stesso username, risulterebbe già occupato.'));
    guaio.aMeta = true;
    guaio.userId = nuovoUserId;
    throw guaio;
  }

  return { userId: nuovoUserId, ...nodo.data };
}

/**
 * Aggiorna utente (es. saldoUtente, fidoRete, saldoBonus).
 * @param {string} userId - ID utente da aggiornare
 * @param {Object} data - Dati da aggiornare
 * @returns {Promise<Object>}
 */
export async function updateUser(userId, data) {
  const base = getBaseUrl();
  if (!base) throw new Error(i18next.t('API URL non configurata'));
  const url = `${base}/private/jurisdiction/${userId}`;
  const res = await axios.put(url, data, { headers: getAuthHeaders() });
  return res.data;
}

/**
 * Crea movimento finanziario (per tracciare operazioni fido/giroconto/bonus).
 * @deprecated Usa giroconto(), fido(), bonus() per operazioni sicure
 * @param {Object} data - Dati movimento
 * @returns {Promise<Object>}
 */
export async function createMovement(data) {
  const base = getBaseUrl();
  if (!base) throw new Error(i18next.t('API URL non configurata'));
  const url = `${base}/api/movements`;
  const res = await axios.post(url, data, { headers: getAuthHeaders() });
  return res.data;
}

/**
 * Esegue un giroconto (versa/preleva) con controlli backend.
 * Usa credit-service Java per performance in produzione.
 * @param {string} toUserId - ID utente destinatario
 * @param {number} amount - Importo
 * @param {'versa'|'preleva'} operazione - Tipo operazione
 * @param {string} [causale] - Causale opzionale
 * @returns {Promise<Object>}
 */
export async function giroconto(toUserId, amount, operazione, causale) {
  // "Versa" porta denaro dal proprio conto a quello dell'altro, "preleva" fa il
  // contrario: è la stessa operazione letta dai due lati, e va detto al servizio
  // chi dà e chi riceve.
  const io = currentUserId();
  const preleva = operazione === 'preleva' || operazione === 'rimuovi';

  return creditApi.giroconto({
    fromUserId: preleva ? toUserId : io,
    toUserId: preleva ? io : toUserId,
    amount,
    // Se l'operatore non scrive nulla non c'è nota: il testo di cosa è successo
    // lo mette la piattaforma. Inventarne uno qui significava salvare come nota
    // una frase che nessuno aveva scritto.
    causale: causale || undefined,
    // Il verso era già calcolato qui sopra e si perdeva una riga dopo: senza,
    // il servizio non può comporre "Versamento a X" o "Prelievo da Y" e nello
    // storico restava scritto "Giroconto".
    operation: preleva ? 'WITHDRAWAL' : 'DEPOSIT',
  });
}

/** Chi sta operando, letto dal token. */
function currentUserId() {
  const token = typeof window !== 'undefined'
      ? window.localStorage.getItem('access_token') : null;
  if (!token) return null;
  try {
    const payload = JSON.parse(
        atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.userId || payload.sub || null;
  } catch (e) {
    return null;
  }
}

/**
 * Assegna/rimuove fido con controlli backend.
 * Usa credit-service Java per performance in produzione.
 * @param {string} toUserId - ID utente destinatario
 * @param {number} amount - Importo
 * @param {'assegna'|'rimuovi'} operazione - Tipo operazione
 * @returns {Promise<Object>}
 */
export async function fido(toUserId, amount, operazione) {
  if (USE_JAVA_CREDIT_API) {
    try {
      return await creditApi.fido({
        toUserId,
        amount,
        operazione,
        causale: `Fido ${operazione}`
      });
    } catch (error) {
      console.warn('[usersFrontendApi] Java credit-service failed, falling back to Node.js:', error.message);
    }
  }
  
  // Fallback a Node.js backend
  const base = getBaseUrl();
  if (!base) throw new Error(i18next.t('API URL non configurata'));
  const url = `${base}/api/financial/fido`;
  const res = await axios.post(url, { toUserId, amount, operazione }, { headers: getAuthHeaders() });
  return res.data;
}

/**
 * Assegna/rimuove bonus con controlli backend.
 * Usa credit-service Java per performance in produzione.
 * @param {string} toUserId - ID utente destinatario
 * @param {number} amount - Importo
 * @param {'assegna'|'rimuovi'} operazione - Tipo operazione
 * @returns {Promise<Object>}
 */
export async function bonus(toUserId, amount, operazione) {
  if (USE_JAVA_CREDIT_API) {
    try {
      return await creditApi.bonus({
        toUserId,
        amount,
        operazione,
        causale: `Bonus ${operazione}`
      });
    } catch (error) {
      console.warn('[usersFrontendApi] Java credit-service failed, falling back to Node.js:', error.message);
    }
  }
  
  // Fallback a Node.js backend
  const base = getBaseUrl();
  if (!base) throw new Error(i18next.t('API URL non configurata'));
  const url = `${base}/api/financial/bonus`;
  const res = await axios.post(url, { toUserId, amount, operazione }, { headers: getAuthHeaders() });
  return res.data;
}

/**
 * Ottieni wallet utente da credit-service Java
 * @param {string} userId - ID utente
 * @returns {Promise<Object>} Wallet con saldi e fido
 */
export async function getWallet(userId) {
  if (USE_JAVA_CREDIT_API) {
    try {
      return await creditApi.getWallet(userId);
    } catch (error) {
      console.warn('[usersFrontendApi] Java credit-service getWallet failed:', error.message);
    }
  }
  return null;
}

/**
 * Verifica saldo sufficiente per operazione
 * @param {string} userId - ID utente
 * @param {number} amount - Importo richiesto
 * @returns {Promise<{canPlay: boolean, disponibilita: number, message: string}>}
 */
export async function checkBalance(userId, amount) {
  if (USE_JAVA_CREDIT_API) {
    try {
      return await creditApi.checkBalance(userId, amount);
    } catch (error) {
      console.warn('[usersFrontendApi] Java credit-service checkBalance failed:', error.message);
    }
  }
  return { canPlay: true, disponibilita: 0, message: i18next.t('Verifica saldo non disponibile') };
}

/**
 * Ottiene i candidati parent (utenti che possono essere parent: Agency, Operator, ecc.)
 * @returns {Promise<Array<{ id: string, username: string, role: string }>>}
 */
export async function getParentCandidates() {
  const base = getBaseUrl();
  if (!base) throw new Error(i18next.t('API URL non configurata'));
  const url = `${base}/admin/users`;
  
  // Carica tutti gli utenti e filtra quelli che possono essere parent
  const roles = ['AGENCY_USER', 'AGENCY', 'NODE_USER', 'OPERATOR', 'SKIN_ADMIN', 'SUPER_ADMIN'];
  const res = await axios.get(url, { params: { limit: 500, page: 1 }, headers: getAuthHeaders() });
  const users = res.data?.users || (Array.isArray(res.data) ? res.data : []);
  
  const candidates = users
    .filter(u => {
      const uRole = (u.role || u.type || '').toUpperCase().replace(/-/g, '_');
      return roles.includes(uRole);
    })
    .map(u => ({
      id: u.id || u.userId || u._id,
      username: u.username || '',
      role: u.role || u.type || ''
    }));
  
  return candidates;
}

/**
 * Ottiene le regole di gerarchia
 * @returns {Promise<{ ruleSets: Array, defaultRuleSetId: string }>}
 */
export async function getHierarchyRules() {
  const base = getBaseUrl();
  if (!base) throw new Error(i18next.t('API URL non configurata'));

  // Quali livelli può avere sotto di sé ciascun livello lo dichiara la
  // piattaforma: un agente apre sub-agenti, un'agenzia apre giocatori. Un
  // elenco scritto qui si sfalderebbe al primo livello nuovo creato dal
  // backoffice.
  try {
    const res = await axios.get(`${base}/admin/types/full`,
        { headers: getAuthHeaders() });

    const tipi = Array.isArray(res.data) ? res.data : [];

    const regole = {};
    for (const tipo of tipi) {
      regole[tipo.name] = {
        label: tipo.label || tipo.name,
        childKind: Array.isArray(tipo.childKind) ? tipo.childKind : [],
        accessType: tipo.accessType,
        // Il colore del livello e' una decisione della piattaforma, non della
        // schermata: viene di qui cosi' backoffice e sito clienti tingono lo
        // stesso livello allo stesso modo, e un livello nuovo si colora da
        // solo senza toccare nessun css.
        color: tipo.color || null,
      };
    }

    return regole;
  } catch (e) {
    console.warn('Livelli non disponibili:', e.message);
    return {};
  }
}


/**
 * Ottiene i dati dell'utente corrente (inclusi networkConstraints)
 * @returns {Promise<Object>}
 */
export async function getCurrentUser() {
  const base = getBaseUrl();
  if (!base) throw new Error(i18next.t('API URL non configurata'));
  const userId = typeof window !== 'undefined' ? getCurrentUserId() : null;
  if (!userId) throw new Error(i18next.t('Utente non autenticato'));
  const url = `${base}/private/jurisdiction/${userId}`;
  const res = await axios.get(url, { headers: getAuthHeaders() });
  return res.data;
}

/**
 * Ottiene i vincoli di rete dell'utente corrente
 * @returns {Promise<Object|null>}
 */
export async function getNetworkConstraints() {
  try {
    const user = await getCurrentUser();
    return user?.networkConstraints || null;
  } catch (e) {
    console.warn('Could not get network constraints:', e.message);
    return null;
  }
}
